import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileAudio, Link2, Loader2, CheckCircle2, XCircle, Mic } from "lucide-react";

const statusMeta = {
  processing: { label: "Transcribing…", cls: "bg-blue-100 text-blue-700", Icon: Loader2, spin: true },
  transcribed: { label: "Transcribed", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700", Icon: XCircle },
  link_only: { label: "Link saved", cls: "bg-zinc-100 text-zinc-600", Icon: Link2 },
};

const StatusPill = ({ status }) => {
  const m = statusMeta[status] || statusMeta.link_only;
  const { Icon } = m;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full text-xs px-2.5 py-0.5 font-[Manrope] font-medium ${m.cls}`}>
      <Icon className={`h-3 w-3 ${m.spin ? "animate-spin" : ""}`} /> {m.label}
    </span>
  );
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export default function Meetings() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/meetings").then((r) => setMeetings(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    const iv = setInterval(() => {
      setMeetings((prev) => {
        if (prev.some((m) => m.status === "processing" || m.analysis_status === "processing")) load();
        return prev;
      });
    }, 4000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2.5">
          <Mic className="h-6 w-6 text-[#193962]" />
          <h1 className="font-[Outfit] font-bold tracking-tight text-4xl sm:text-5xl">Meetings & Intelligence</h1>
        </div>
        <p className="text-base text-muted-foreground font-[Manrope] mt-2">
          {`${meetings.length} ${meetings.length === 1 ? "recording" : "recordings"} across all clients. AI transcribes uploaded audio/video automatically.`}
        </p>

        <div className="mt-8 border border-border bg-white rounded-md overflow-hidden" data-testid="all-meetings-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="font-[Manrope]">Title</TableHead>
                <TableHead className="font-[Manrope]">Client</TableHead>
                <TableHead className="font-[Manrope]">Date</TableHead>
                <TableHead className="font-[Manrope]">Source</TableHead>
                <TableHead className="font-[Manrope]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground font-[Manrope]">Loading…</TableCell></TableRow>
              ) : meetings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-16">
                    <FileAudio className="h-8 w-8 mx-auto text-zinc-300 mb-3" />
                    <p className="font-[Manrope] text-muted-foreground">No recordings yet. Add one from a client's Meetings tab.</p>
                  </TableCell>
                </TableRow>
              ) : (
                meetings.map((m) => (
                  <TableRow key={m.meeting_id} data-testid={`all-meeting-row-${m.meeting_id}`} className="cursor-pointer" onClick={() => navigate(`/clients/${m.client_id}`)}>
                    <TableCell className="font-[Manrope] font-medium" data-no-i18n>{m.title}</TableCell>
                    <TableCell className="font-[Manrope] text-sm text-[#193962] hover:underline">{m.client_name}</TableCell>
                    <TableCell className="font-[Manrope] text-sm">{fmtDate(m.meeting_date)}</TableCell>
                    <TableCell className="font-[Manrope] text-sm flex items-center gap-1.5">
                      {m.source_type === "link" ? <Link2 className="h-3.5 w-3.5" /> : <FileAudio className="h-3.5 w-3.5" />}
                      {m.source_type === "link" ? "Meet link" : "Upload"}
                    </TableCell>
                    <TableCell><StatusPill status={m.status} /></TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
